import { GetPageProps, UserRegistrationState } from 'hooks/u002/useUserRegistrationHooks';
import { FC, useContext, useEffect, useState } from 'react';
import { Button } from 'atoms/Button';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { PageContent } from 'templates/PageContent';
import { Form } from 'templates/Form';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { SubmitHandler, useForm } from 'react-hook-form';
import { PhoneNumberSchemaType } from 'commons/auditionForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePhoneVerificationSchema } from 'hooks/u002/usePhoneVerificationSvhema';
import { usePhoneVerification } from 'hooks/u002/usePhoneVerificationHooks';
import { useLocation, useNavigate } from 'react-router-dom';
import MessageModal from 'organisms/MessageModal';
import { ExsitingUserDialog } from 'organisms/u002/ExsitingUserDialog';
import { AppContext } from 'templates/AppContextProvider';
import { Country, getCountryCallingCode } from 'react-phone-number-input';
import LoadingModal from 'organisms/LoadingModal';
import { SelectCountryNumber } from 'atoms/SelectCountryNumber';
import { FormSectionTitle } from 'atoms/FormSectionTitle';
import { UnauthAppFooter } from 'templates/UnauthAppFooter';
import { PATHS } from 'commons/paths';
import { useValidatePhoneSession } from 'hooks/u002/useValidatePhoneSession';
import { PopupErrorDialog } from 'organisms/u002/PopupErrorDialog';

//U002F07 電話番号認証画面
type PhoneVerificationProps = GetPageProps<UserRegistrationState, 'PhoneVerification'>;
export const PhoneVerification: FC<PhoneVerificationProps> = (props) => {
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const PhoneVerificationSchema = usePhoneVerificationSchema();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get('sessionId');
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneVerification, phoneVerificationLoading] = usePhoneVerification();
  const [succcessPhoneNumber, setSuccessPhoneNumber] = useState('');
  const { isGlobal, currentLang } = useContext(AppContext);
  const [, validatePhoneSessionLoading, validatePhoneSessionErr] = useValidatePhoneSession(
    sessionId ?? ''
  );
  const [validateErrorMessage, setValidateErrorMessage] = useState('');
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState(isGlobal ? 'US' : 'JP');
  const {
    setValue,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<PhoneNumberSchemaType>({
    resolver: zodResolver(PhoneVerificationSchema),
    shouldFocusError: true,
    defaultValues: {
      phoneNumber: '',
    },
  });
  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);

  const onSubmit: SubmitHandler<PhoneNumberSchemaType> = async (form) => {
    if (!sessionId) return;
    console.log('form', form);
    await phoneVerification({
      request: {
        phoneNumber: form.phoneNumber,
        sessionId: sessionId,
      },
      onSuccess: (response) => {
        if (response.exsting_user_flg === '1') {
          setSuccessPhoneNumber(form.phoneNumber);
        } else {
          props.onNext(form.phoneNumber);
        }
      },
      onError: (error) => {
        console.log('error', error);
        setErrorMessage(
          error.message === 'MU002F0129E' ? tAuditionForm('TU002_115') : tAuditionForm('TU002_050')
        );
      },
    });
  };
  useEffect(() => {
    if (validatePhoneSessionErr) {
      setValidateErrorMessage(
        validatePhoneSessionErr?.message === 'MU002F0137E'
          ? tAuditionForm('TU002_123')
          : tAuditionForm('TU002_102')
      );
    }
  }, [validatePhoneSessionErr, tAuditionForm]);
  return (
    <>
      {validateErrorMessage && <PopupErrorDialog message={validateErrorMessage} />}
      {succcessPhoneNumber && (
        <ExsitingUserDialog
          onNext={() => {
            props.onNext(succcessPhoneNumber);
          }}
          onLogin={() => {
            const toParam = new URLSearchParams();
            toParam.set('redirect', window.location.pathname);
            const to = PATHS.LOGIN + '?' + toParam.toString();
            navigate(to);
          }}
        />
      )}
      <AppLogoHeader />
      {(phoneVerificationLoading || validatePhoneSessionLoading) && <LoadingModal />}
      <PageContent>
        {errorMessage && (
          <MessageModal
            message={errorMessage}
            closeLabel={tAuditionForm('TU002_032')}
            onClose={() => setErrorMessage('')}
          />
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form title={tAuditionForm('TU002_072')}>
            <div className="flex flex-col items-center justify-center w-full">
              {/* 日本の電話番号表示（国識別番号表示なし） */}
              {!isGlobal && (
                <>
                  <p className="text-[--c-textinput-text] text-sm">{tAuditionForm('TU002_133')}</p>
                  <div className="w-full mx-auto max-w-[18rem] mt-4">
                    <FormSectionTitle text={tAuditionForm('TU002_012')} htmlFor={'phoneNumber'} />
                  </div>
                  <div className="flex items-center justify-start w-full max-w-[18rem] mx-auto mt-2">
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      inputMode="numeric"
                      className="flex-grow border-[--c-textinput-border] text-[--c-textinput-text] bg-[--c-textinput-background] rounded-[8px] h-[40px] px-2 border-[1px]"
                      placeholder="08012345678"
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const countryCallingCode = getCountryCallingCode(countryCode as Country);
                        const formattedValue = inputValue
                          ? `+${countryCallingCode}${inputValue}`
                          : '';
                        setValue('phoneNumber', formattedValue, { shouldValidate: true });
                      }}
                    />
                  </div>
                </>
              )}
              {/* 海外の電話番号表示（国識別番号表示あり） */}
              {isGlobal && (
                <>
                  <p className="text-[--c-textinput-text] text-sm">{tAuditionForm('TU002_133')}</p>
                  <div className="w-full max-w-md mx-auto mt-4">
                    <FormSectionTitle text={tAuditionForm('TU002_012')} htmlFor={'phoneNumber'} />
                  </div>
                  <div className="flex items-center justify-start w-full max-w-md mx-auto mt-2">
                    <SelectCountryNumber
                      countryCode={countryCode}
                      setCountryCode={(code) => {
                        setCountryCode(code);
                        // 国選択後に既存の入力値があれば、国番号を更新
                        const phoneNumberValue = document.getElementById(
                          'phoneNumber'
                        ) as HTMLInputElement;
                        if (phoneNumberValue && phoneNumberValue.value) {
                          const countryCallingCode = getCountryCallingCode(code as Country);
                          const formattedValue = phoneNumberValue.value
                            ? `+${countryCallingCode}${phoneNumberValue.value}`
                            : '';
                          setValue('phoneNumber', formattedValue, { shouldValidate: true });
                        }
                      }}
                      className="w-32 text-[--c-textinput-text] bg-[--c-textinput-background] mr-2"
                    />
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      inputMode="numeric"
                      className="w-full border-[--c-textinput-border] text-[--c-textinput-text] bg-[--c-textinput-background] rounded-[8px] h-[40px] ml-2 px-2 border-[1px]"
                      placeholder="08012345678"
                      onChange={(e) => {
                        const countryCallingCode = getCountryCallingCode(countryCode as Country);
                        const formattedValue = e.target.value
                          ? `+${countryCallingCode}${e.target.value}`
                          : '';
                        setValue('phoneNumber', formattedValue, { shouldValidate: true });
                      }}
                    />
                  </div>
                </>
              )}
              <FieldErrorMessages messages={[errors.phoneNumber?.message]} />
              <Button
                className="w-[10rem] mt-20 mb-2"
                label={tAuditionForm('TU002_037')}
                type="submit"
              />
            </div>
          </Form>
        </form>
      </PageContent>
      <UnauthAppFooter />
    </>
  );
};
